// @givmo/passkeys — where "Lost your authenticator?" leads (D-AS-21's approved B2d-lost; AS-WEB18).
//
// The CONTENT of a dialog — the "Confirm it's you" sheet's, through a host's `renderLost` — never a
// dialog of its own, like the reset's screens beside it. THE STEPS:
//   1. CHOOSE (B2d-lost, as drawn): the title, the act's line, "Use a recovery code" and "Email me a
//      reset", each with its helper, and Back to the sheet. The line and the helpers are the act's
//      own when its row names them (`StepUpLostLines`: Board's signing says what signing waits on),
//      else the consoles' (`LOST_AUTHENTICATOR_LINES`). An account whose read says no recovery code is
//      left sees that way out closed, with "You don’t have any recovery codes to use." (D-AS-39): the
//      server would refuse every code with its one uniform 400, so it is never offered as if it could
//      work.
//   2. A RECOVERY CODE: iOS's recovery step — one field, "Use recovery code", Back. The code goes to
//      `POST /authn/mfa/recovery/redeem` with the signed-in account's own address, and the
//      authenticator app is removed AT ONCE — only it: the backend's `redeem_recovery_code` unenrols
//      the TOTP factor and touches no passkey, so nothing here says passkeys went with it.
//   3. REMOVED: "Your authenticator app was removed" and what happens next (the act's line, else the
//      consoles'). Done closes: in the sheet the act is cancelled like any dismissal, since Board's
//      signing reopens only 24 hours after a removal, and going back to the sheet could only meet its
//      refusal. ⚠ AND SIGNS THE CONSOLE OUT (AS-BE63): the redemption removed a second factor, so the
//      backend ended every session of the account, the suite's included, as Identity Platform ended
//      every Firebase one. Leaving the result — Done, or the sheet's own dismissal — signs out through
//      the host's own `signOut` (which also releases the suite session; `sign-out-after-removal.ts`),
//      and the person signs in again without a code: the line's promise, now true on reload too. So
//      nothing is re-read after the code: the read would answer 401, and the card would go on showing
//      an authenticator the account no longer holds. A host that hands no `signOut` keeps the old
//      fresh re-read (so a host that only observes the cache, AS-WEB15, sees it gone).
//   "Email me a reset" opens the two-factor reset (AS-WEB13's `FactorResetFlow`), its Back here.
//
// ⚠ REFUSALS are the server's: its uniform 400 for a wrong, spent or unknown code, and its 429, each in
// its own sentence; the dark 404 in the apps' "Recovery isn't available right now…" (authenticator
// apps dark: `redeem` follows `ACCOUNT_SECURITY_MFA_ENABLED` alone). A refused code stays in the field,
// so a slip can be fixed: a wrong code spends nothing.
// ⚠ FOCUS FOLLOWS THE STEP (`step-focus.ts`): each step after the first focuses its title, the code
// step its field, and a refused code puts focus back in the field.

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, ErrorMessage, TextField, cn, testIdProp } from '@givmo/design-system';
import { isGivmoError } from '@givmo/api-client';
import type { StepUpLostLines, StepUpLostWayOut } from '@givmo/step-up';
import {
  fetchAccountFactors,
  invalidateAccountFactors,
  recordFactorsRemoved,
} from './account-factors';
import {
  FACTOR_RESET_STEP_WORDS,
  LOST_AUTHENTICATOR_LINES,
  LOST_AUTHENTICATOR_WORDS as WORDS,
  RECOVERY_CODE_WORDS as CODE_WORDS,
} from './factor-reset-copy';
import { FactorResetFlow } from './factor-reset-flow';
import { redeemRecoveryCode } from './factor-reset-transport';
import { keepNumbersWhole } from './keep-numbers-whole';
import { useSignOutAfterRemoval, type RemovalSignOut } from './sign-out-after-removal';
import { useStepTitleFocus } from './step-focus';

const PREFIX = 'lost-authenticator';

const TITLE = 'font-sans text-s18 font-w700 tracking-givmo text-text-strong outline-none';
const LINE = 'font-sans text-s14 font-w400 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const LINK =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline disabled:opacity-60';

export interface LostAuthenticatorFlowProps {
  /** The signed-in account's own address: the redeem and the reset name it. */
  email: string;
  /** The signed-in account's uid, for the fresh re-read after a code; absent, the reads are invalidated. */
  uid?: string | null;
  /** The account's unused recovery codes, from its security read; `undefined` while unknown. */
  recoveryCodesLeft: number | undefined;
  /** For the reset's words (`factorResetWords`): whether the account holds a passkey. */
  holdsPasskey: boolean | undefined;
  /** For the reset's words: whether this account can set an authenticator app up again here. */
  authenticatorOffered: boolean;
  /** The armed reset's deadline, if any: past it, the emailed reset opens on its finish (AS-WEB17). */
  pendingReset?: Date | null | undefined;
  /** The act's own lines (`StepUpLostProps.lines`); each one absent is the consoles'. */
  lines?: StepUpLostLines;
  /** Open on one way out rather than the choice of both (`StepUpLostProps.wayOut`). */
  wayOut?: StepUpLostWayOut;
  /**
   * The host's own sign-out (`useAuth().signOut`, which also releases the suite session): a spent
   * recovery code, or a finished reset, ends every session of the account (AS-BE63), so leaving the
   * result signs this console out.
   */
  signOut?: RemovalSignOut | undefined;
  /** Back to the sheet; absent (a surface of its own), the choice offers Cancel. */
  onBack?: () => void;
  /** Close: Done, Cancel, and the sheet's own dismissal. */
  onClose: () => void;
}

type Step = 'choose' | 'recoveryCode' | 'removed' | 'emailReset';

/** The words a refused code shows: the dark surface the apps' line, else the server's own sentence. */
function refusalLine(thrown: unknown): string {
  if (isGivmoError(thrown)) {
    if (thrown.status === 404) return FACTOR_RESET_STEP_WORDS.unavailable;
    if (thrown.message.length > 0) return thrown.message;
  }
  return 'That didn’t work. Try again.';
}

function TextLink({
  children,
  onClick,
  disabled,
  testId,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  testId: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={LINK}
      {...testIdProp(testId)}
    >
      {children}
    </button>
  );
}

export function LostAuthenticatorFlow({
  email,
  uid,
  recoveryCodesLeft,
  holdsPasskey,
  authenticatorOffered,
  pendingReset,
  lines,
  wayOut,
  signOut,
  onBack,
  onClose,
}: LostAuthenticatorFlowProps) {
  const queryClient = useQueryClient();
  // The act's own lines win, line by line; the consoles' fill the rest.
  const said = { ...LOST_AUTHENTICATOR_LINES, ...definedLines(lines) };
  const noCodesLeft = recoveryCodesLeft === 0;
  const [step, setStep] = useState<Step>(() =>
    wayOut === 'emailReset' ? 'emailReset' : wayOut === 'recoveryCode' ? 'recoveryCode' : 'choose',
  );
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Focus follows the step (`step-focus.ts`): the code step's field takes it itself.
  const title = useStepTitleFocus(step);
  const codeField = useRef<HTMLInputElement>(null);
  const { owe: oweSignOut, settle: settleSignOut } = useSignOutAfterRemoval(signOut);
  // A way out opened directly (`wayOut`) has no choice behind it: its Back is the opener's, and a
  // surface with none offers Cancel instead.
  const backFromWayOut: (() => void) | undefined =
    wayOut === undefined ? () => setStep('choose') : onBack;

  const redeem = useCallback(async () => {
    const typed = code.trim();
    if (busy || typed.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await redeemRecoveryCode(email, typed);
    } catch (thrown) {
      setError(refusalLine(thrown));
      setBusy(false);
      // Back to the code, which stays in the field to be fixed: the button that sent it lost focus.
      codeField.current?.focus();
      return;
    }
    // The authenticator is gone, and with it this session (AS-BE63): with a sign-out to finish on,
    // it is owed from here, and nothing is re-read (the read would answer 401) — the card behind the
    // result takes the redemption's own answer instead (`recordFactorsRemoved`). Otherwise read the
    // account again before saying so, fresh where the uid is known (a host observing the cache only
    // sees a fetch).
    if (signOut !== undefined) {
      oweSignOut();
      if (typeof uid === 'string' && uid.length > 0) {
        recordFactorsRemoved(queryClient, uid, { passkeysAndReset: false });
      }
    } else {
      if (typeof uid === 'string' && uid.length > 0) await fetchAccountFactors(queryClient, uid);
      await invalidateAccountFactors(queryClient);
    }
    setCode('');
    setBusy(false);
    setStep('removed');
  }, [busy, code, email, queryClient, uid, signOut, oweSignOut]);

  if (step === 'emailReset') {
    return (
      <FactorResetFlow
        kind="twoFactor"
        email={email}
        alsoTurnsOffTwoFactor
        authenticatorOffered={authenticatorOffered}
        holdsPasskey={holdsPasskey}
        pendingReset={pendingReset}
        uid={uid}
        signOut={signOut}
        {...(backFromWayOut !== undefined ? { onBack: backFromWayOut } : {})}
        onClose={onClose}
      />
    );
  }

  if (step === 'removed') {
    return (
      <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-removed`)}>
        <Dialog.Title ref={title} tabIndex={-1} className={TITLE}>
          {CODE_WORDS.resultTitle}
        </Dialog.Title>
        <Dialog.Description className={LINE} {...testIdProp(`${PREFIX}-removed-line`)}>
          {keepNumbersWhole(said.recoveryCodeResult)}
        </Dialog.Description>
        <Button
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          onClick={() => {
            onClose();
            settleSignOut();
          }}
          testId={`${PREFIX}-done`}
        >
          {CODE_WORDS.done}
        </Button>
      </div>
    );
  }

  if (step === 'recoveryCode') {
    return (
      <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-code-step`)}>
        <Dialog.Title className={TITLE}>{CODE_WORDS.title}</Dialog.Title>
        <Dialog.Description className={LINE}>{CODE_WORDS.line}</Dialog.Description>
        <form
          className="flex flex-col gap-14"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void redeem();
          }}
          {...testIdProp(`${PREFIX}-code-form`)}
        >
          <TextField
            ref={codeField}
            label={CODE_WORDS.label}
            value={code}
            autoFocus
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={64}
            // Recovery codes print in A3's wide tracking; typed off paper, they read the same here.
            className="tracking-wider"
            error={error !== null}
            onChange={(e) => {
              setCode(e.target.value);
              if (error !== null) setError(null);
            }}
            testId={`${PREFIX}-code`}
          />
          {error !== null && (
            <ErrorMessage className="pt-0" testId={`${PREFIX}-error`}>
              {error}
            </ErrorMessage>
          )}
          <Button
            type="submit"
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            loading={busy}
            disabled={code.trim().length === 0}
            testId={`${PREFIX}-redeem`}
          >
            {CODE_WORDS.submit}
          </Button>
        </form>
        <div className="flex items-center">
          <TextLink
            disabled={busy}
            onClick={() => {
              setError(null);
              (backFromWayOut ?? onClose)();
            }}
            testId={backFromWayOut !== undefined ? `${PREFIX}-code-back` : `${PREFIX}-code-cancel`}
          >
            {backFromWayOut !== undefined ? CODE_WORDS.back : FACTOR_RESET_STEP_WORDS.cancel}
          </TextLink>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-16" {...testIdProp(PREFIX)}>
      <Dialog.Title ref={title} tabIndex={-1} className={TITLE}>
        {WORDS.title}
      </Dialog.Title>
      <Dialog.Description className={LINE} {...testIdProp(`${PREFIX}-line`)}>
        {keepNumbersWhole(said.line)}
      </Dialog.Description>
      <div className="flex flex-col gap-18">
        <div className="flex flex-col gap-6">
          <Button
            appearance="saas"
            tone="secondary"
            size="lg"
            fullWidth
            disabled={noCodesLeft}
            onClick={() => {
              setError(null);
              setCode('');
              setStep('recoveryCode');
            }}
            testId={`${PREFIX}-recovery-code`}
          >
            {WORDS.recoveryCode}
          </Button>
          <p className={QUIET} {...testIdProp(`${PREFIX}-recovery-code-helper`)}>
            {keepNumbersWhole(noCodesLeft ? WORDS.noCodesLeft : said.recoveryCode)}
          </p>
        </div>
        <div className="flex flex-col gap-6">
          <Button
            appearance="saas"
            tone="secondary"
            size="lg"
            fullWidth
            onClick={() => setStep('emailReset')}
            testId={`${PREFIX}-email-reset`}
          >
            {WORDS.emailReset}
          </Button>
          <p className={QUIET} {...testIdProp(`${PREFIX}-email-reset-helper`)}>
            {keepNumbersWhole(said.emailReset)}
          </p>
        </div>
      </div>
      <div className={cn('flex flex-wrap items-center justify-between gap-8')}>
        <TextLink
          onClick={onBack ?? onClose}
          testId={onBack !== undefined ? `${PREFIX}-back` : `${PREFIX}-cancel`}
        >
          {onBack !== undefined ? WORDS.back : FACTOR_RESET_STEP_WORDS.cancel}
        </TextLink>
      </div>
    </div>
  );
}

/** The lines a row actually supplied: an empty or missing one never hides the consoles' own. */
function definedLines(lines: StepUpLostLines | undefined): StepUpLostLines {
  const kept: StepUpLostLines = {};
  if (lines === undefined) return kept;
  for (const key of ['line', 'recoveryCode', 'emailReset', 'recoveryCodeResult'] as const) {
    const value = lines[key];
    if (typeof value === 'string' && value.length > 0) kept[key] = value;
  }
  return kept;
}
